import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiShoppingCart, FiX } from "react-icons/fi";
import { HiMinus, HiPlus } from "react-icons/hi";
import Swal from "sweetalert2";
import { useAppDispatch } from "../../redux/hook";
import {
  addCartProduct,
  getCartCount,
  getTotalAmount,
  type CartItemType,
} from "../../redux/slice/useCartSlice";
import { formatCurrency, getImage, truncateCaractere } from "../../utils/Utils";
import {
  formatVariationUnitPrice,
  getProductPromoRate,
  getSizeVariationsForPicker,
  getUnitPriceForVariation,
  getCartStockLimit,
  getMaxOrderQuantity,
  isStockTrackingEnabled,
  isVariationStockUnlimited,
} from "../../utils/productPricing";
import { Color } from "../../utils/theme";

function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < breakpoint,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, [breakpoint]);
  return isMobile;
}

function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export interface ChooseSizeModalProps {
  product: any;
  devise?: string;
  imageUrl?: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ChooseSizeModal: React.FC<ChooseSizeModalProps> = ({
  product,
  devise = "XOF",
  imageUrl,
  isOpen,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const promoRate = getProductPromoRate(product);
  const sizeOptions = getSizeVariationsForPicker(product);

  const [selectedVariation, setSelectedVariation] = useState<any>(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (isOpen && sizeOptions.length > 0) {
      setSelectedVariation(sizeOptions[0]);
      setQuantity(1);
    }
  }, [isOpen, product?.id, sizeOptions.length]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen || !product) return null;

  const unitPrice = getUnitPriceForVariation(
    product,
    selectedVariation,
    promoRate,
  );
  const totalPrice = unitPrice * quantity;
  const stockUnlimited =
    !isStockTrackingEnabled(product) ||
    isVariationStockUnlimited(selectedVariation);
  const maxQty = stockUnlimited
    ? 99999
    : Math.max(0, getMaxOrderQuantity(product, selectedVariation));

  const handleAddToCart = () => {
    if (!selectedVariation?.id) return;
    if (!stockUnlimited && maxQty <= 0) return;

    const produit: CartItemType = {
      produit: product,
      prix: unitPrice,
      quantite: quantity,
      stock: getCartStockLimit(product, selectedVariation),
      variations: [selectedVariation],
      vendeur:
        typeof product?.vendeur === "object"
          ? product?.vendeur?.id
          : product?.vendeur,
    };

    dispatch(addCartProduct(produit));
    dispatch(getCartCount());
    dispatch(getTotalAmount());
    onClose();

    Swal.fire({
      icon: "success",
      iconColor: Color.success,
      confirmButtonColor: Color.success,
      title: "Produit ajouté au panier avec succès !",
      showConfirmButton: false,
      timer: 2000,
    });
  };

  const plainDescription = truncateCaractere(stripHtml(product?.description), 80);

  return createPortal(
    <div className="choose-size-modal" onClick={onClose} role="presentation">
      <div className="choose-size-backdrop" aria-hidden />
      <div
        className="choose-size-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="choose-size-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="choose-size-panel__header">
          <div className="choose-size-panel__product-preview">
            {imageUrl && (
              <img
                src={getImage(imageUrl)}
                alt={product?.nom ?? ""}
                className="choose-size-panel__thumb"
              />
            )}
            <div>
              <h3 id="choose-size-title" className="choose-size-panel__title">
                {product?.nom}
              </h3>
              {plainDescription && (
                <p className="choose-size-panel__subtitle">{plainDescription}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            className="choose-size-panel__close"
            onClick={onClose}
            aria-label="Fermer"
          >
            <FiX />
          </button>
        </div>

        <div className="choose-size-panel__sizes">
          <p className="choose-size-panel__section-label">Choisissez une taille</p>
          <div
            className={
              isMobile
                ? "choose-size-panel__size-grid"
                : "choose-size-panel__size-list"
            }
          >
            {sizeOptions.length === 0 ? (
              <p className="choose-size-panel__empty">
                Aucune taille disponible pour le moment.
              </p>
            ) : (
              sizeOptions.map((variation) => {
                const varPrice = getUnitPriceForVariation(
                  product,
                  variation,
                  promoRate,
                );
                const unitLabel = formatVariationUnitPrice(
                  varPrice,
                  variation.taille,
                );
                const isSelected = selectedVariation?.id === variation.id;

                return (
                  <button
                    key={variation.id ?? variation.taille}
                    type="button"
                    className={`choose-size-option ${isSelected ? "choose-size-option--selected" : ""}`}
                    onClick={() => {
                      setSelectedVariation(variation);
                      setQuantity(1);
                    }}
                  >
                    {!isMobile && (
                      <span
                        className={`choose-size-option__radio ${isSelected ? "choose-size-option__radio--checked" : ""}`}
                        aria-hidden
                      />
                    )}
                    <span className="choose-size-option__size">
                      {variation.taille}
                    </span>
                    <span className="choose-size-option__price-block">
                      <span className="choose-size-option__price">
                        {formatCurrency(varPrice, devise)}
                      </span>
                      {unitLabel && (
                        <span className="choose-size-option__unit">{unitLabel}</span>
                      )}
                    </span>
                    {isMobile && isSelected && (
                      <span className="choose-size-option__check" aria-hidden>
                        ✓
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="choose-size-panel__footer">
          <div className="choose-size-panel__qty-block">
            <span className="choose-size-panel__label">Quantité</span>
            <div className="choose-size-qty-stepper">
              <button
                type="button"
                className="choose-size-qty-stepper__btn"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                disabled={quantity <= 1}
                aria-label="Diminuer la quantité"
              >
                <HiMinus />
              </button>
              <span className="choose-size-qty-stepper__value">{quantity}</span>
              <button
                type="button"
                className="choose-size-qty-stepper__btn"
                onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                disabled={quantity >= maxQty}
                aria-label="Augmenter la quantité"
              >
                <HiPlus />
              </button>
            </div>
          </div>

          <div className="choose-size-panel__total-block">
            <span className="choose-size-panel__label">Prix total</span>
            <span className="choose-size-panel__total">
              {formatCurrency(totalPrice, devise)}
            </span>
          </div>
        </div>

        <button
          type="button"
          className="choose-size-panel__add-btn"
          onClick={handleAddToCart}
          disabled={!selectedVariation?.id || (!stockUnlimited && maxQty <= 0)}
        >
          <FiShoppingCart />
          Ajouter au panier
        </button>

        {isMobile && (
          <div className="choose-size-panel__trust">
            <div className="choose-size-panel__trust-item">
              <span className="choose-size-panel__trust-icon">🛡</span>
              <span>Produit naturel — Sans additifs chimiques</span>
            </div>
            <div className="choose-size-panel__trust-item">
              <span className="choose-size-panel__trust-icon">🌿</span>
              <span>Bon pour votre santé — Riche en nutriments</span>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
